import React, { useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { useAdminAuth } from './auth';
import { setUnauthorizedHandler } from './api';
import MenuTree      from './components/MenuTree';
import MovieList     from './components/MovieList';
import GenreManager  from './components/GenreManager';
import './AdminApp.css';

// ── Gate screens ───────────────────────────────────────────────

function GateScreen({ title, message }) {
  return (
    <div className="admin-gate">
      <div className="admin-gate-box">
        <h2>{title}</h2>
        <p>{message}</p>
      </div>
    </div>
  );
}

// ── Sidebar layout ─────────────────────────────────────────────

function AdminLayout({ children, signOut }) {
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          ICON Admin
          <span>Game Menu CMS</span>
        </div>
        <nav className="admin-nav">
          <NavLink to="/admin/menu"   className={({ isActive }) => isActive ? 'active' : ''}>
            Menu Items
          </NavLink>
          <NavLink to="/admin/movies" className={({ isActive }) => isActive ? 'active' : ''}>
            Movies
          </NavLink>
          <NavLink to="/admin/genres" className={({ isActive }) => isActive ? 'active' : ''}>
            Genres
          </NavLink>
        </nav>
        <div className="admin-sidebar-footer">
          <button className="admin-signout-btn" onClick={signOut}>Sign out</button>
        </div>
      </aside>
      <main className="admin-main">{children}</main>
    </div>
  );
}

// ── AdminApp root ──────────────────────────────────────────────

export default function AdminApp() {
  const { status, signOut } = useAdminAuth();
  const navigate = useNavigate();

  // Register 401 handler so expired mid-session requests redirect to expired screen
  useEffect(() => {
    setUnauthorizedHandler(() => navigate('/admin', { replace: true }));
  }, [navigate]);

  if (status === 'checking') {
    return (
      <GateScreen
        title="Verifying access…"
        message="Please wait while your credentials are verified."
      />
    );
  }

  if (status === 'expired') {
    return (
      <GateScreen
        title="Session expired"
        message="Your admin link has expired. Please return to the ICON platform and request a new link."
      />
    );
  }

  if (status === 'blocked') {
    return (
      <GateScreen
        title="Access denied"
        message="This page requires an admin token. Please use the link provided by the ICON platform."
      />
    );
  }

  // status === 'allowed'
  return (
    <AdminLayout signOut={signOut}>
      <Routes>
        <Route path="menu"   element={<MenuTree />} />
        <Route path="movies" element={<MovieList />} />
        <Route path="genres" element={<GenreManager />} />
        <Route path="*"      element={<Navigate to="menu" replace />} />
      </Routes>
    </AdminLayout>
  );
}
